import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ChevronDown, Flag, Globe, Play } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { useBreadcrumbs } from '@/components/Breadcrumbs/useBreadcrumbs';
import { InvoiceRunType } from '@/graphql/generatedTypes';
import { Routes } from '@/utils/routes';

import { useInvoiceRunsQuery } from './InvoiceRuns.generated';
import { ChargeModal, type ChargeModalRun } from './ChargeModal';
import { RunHistoryTable, type RunRow } from './RunHistoryTable';
import { StartRunSheet, type StartRunType } from './StartRunSheet';
import { CapabilityChip, GatedButton } from './capability';
import { useInvoicingCapability } from './useInvoicingCapability';
import {
  formatAgoSince,
  formatCount,
  formatDateLong,
  formatDateTimeShort,
  formatDayMonth,
  isEsStylePeriod,
  isReadyToCharge,
  isRunActive,
  periodLabel,
  runPresentationState,
  runTypeLongLabel,
} from './invoiceRunPresentation';

function LiveChip({ lastRefreshed }: { lastRefreshed: number }) {
  // Re-render every few seconds so "refreshed Xs ago" stays honest.
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = window.setInterval(() => setTick((t) => t + 1), 5_000);
    return () => window.clearInterval(interval);
  }, []);
  return (
    <span className="tw:inline-flex tw:h-7 tw:items-center tw:gap-1.5 tw:rounded-full tw:border tw:border-border tw:px-2.5 tw:text-xs tw:text-muted-foreground">
      <span className="tw:inline-block tw:size-2 tw:animate-pulse tw:rounded-full tw:bg-sky-600" />
      Live · refreshed {formatAgoSince(lastRefreshed)}
    </span>
  );
}

function WaitingForYouCard({
  run,
  onReview,
  onCharge,
  capability,
}: {
  run: RunRow;
  onReview: () => void;
  onCharge: () => void;
  capability: ReturnType<typeof useInvoicingCapability>;
}) {
  const period = periodLabel(isEsStylePeriod(run), run.year, run.iso_month);
  const errorText =
    !run.error_count || run.error_count === 0
      ? 'no errors'
      : `${formatCount(run.error_count)} error${run.error_count === 1 ? '' : 's'}`;
  // processed_count counts every store the run looked at, so only
  // invoice_count may be called invoices.
  const volumeText =
    run.invoice_count != null
      ? `${formatCount(run.invoice_count)} invoices created`
      : `${formatCount(run.processed_count ?? 0)} stores checked`;
  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4 tw:rounded-lg tw:border tw:border-border tw:border-l-[3px] tw:border-l-emerald-500 tw:bg-card tw:p-4">
      <div className="tw:flex tw:flex-col tw:gap-1">
        <p className="tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-muted-foreground">
          Waiting for you
        </p>
        <p className="tw:text-base tw:font-semibold">
          {period} · {runTypeLongLabel(run.type)} is ready to charge
        </p>
        <p className="tw:text-sm tw:text-muted-foreground">
          {volumeText}, {errorText}
          {run.finished_at
            ? ` · finished ${formatDateTimeShort(run.finished_at)}`
            : ''}{' '}
          · reviewed in Billomat
        </p>
      </div>
      <div className="tw:flex tw:items-center tw:gap-2">
        <Button variant="outline" onClick={onReview}>
          Review results
        </Button>
        <GatedButton capability={capability} onClick={onCharge}>
          Complete &amp; charge
        </GatedButton>
      </div>
    </div>
  );
}

function UpcomingCard({ run }: { run: RunRow }) {
  const isEs = run.type === InvoiceRunType.Es;
  const Icon = isEs ? Flag : Globe;
  return (
    <div className="tw:flex tw:items-center tw:gap-3 tw:rounded-lg tw:border tw:border-dashed tw:border-border tw:bg-muted/40 tw:p-4">
      <span className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md tw:bg-muted">
        <Icon className="tw:size-4 tw:text-muted-foreground" />
      </span>
      <div className="tw:flex tw:flex-col">
        <span className="tw:text-sm tw:font-medium">
          {runTypeLongLabel(run.type)}
        </span>
        <span className="tw:text-sm tw:text-muted-foreground">
          {run.due_at
            ? `Starts ${formatDateLong(run.due_at)}, in the evening`
            : 'Scheduled'}
        </span>
        <span className="tw:text-xs tw:text-muted-foreground">
          {periodLabel(isEs, run.year, run.iso_month)}
        </span>
      </div>
    </div>
  );
}

export default function OverviewPage() {
  const history = useHistory();
  const capability = useInvoicingCapability();
  const [startType, setStartType] = useState<StartRunType | null>(null);
  const [chargeRun, setChargeRun] = useState<ChargeModalRun | null>(null);

  useBreadcrumbs({
    id: 'monthly-invoicing',
    items: [
      { label: 'Finance' },
      { label: 'Monthly invoicing', href: Routes.monthlyInvoicing() },
    ],
  });

  const [lastRefreshed, setLastRefreshed] = useState(() => Date.now());
  const { data, loading, refetch, startPolling, stopPolling } =
    useInvoiceRunsQuery({
      variables: { limit: 200 },
      fetchPolicy: 'cache-and-network',
      // So onCompleted also fires per poll, keeping the live chip honest.
      notifyOnNetworkStatusChange: true,
      onCompleted: () => setLastRefreshed(Date.now()),
    });
  const runs = useMemo(() => data?.invoiceRuns ?? [], [data]);

  // ~30 s self-refresh while anything is pending-due or running.
  const anyActive = runs.some((run) => isRunActive(run));
  useEffect(() => {
    if (!anyActive) return;
    startPolling(30_000);
    return () => stopPolling();
  }, [anyActive, startPolling, stopPolling]);

  const scheduled = useMemo(
    () =>
      runs
        .filter((run) => runPresentationState(run) === 'scheduled')
        .sort(
          (a, b) =>
            Date.parse(a.due_at ?? a.enqueued_at) -
            Date.parse(b.due_at ?? b.enqueued_at),
        ),
    [runs],
  );

  const historyRows = useMemo(
    () =>
      runs
        .filter((run) => runPresentationState(run) !== 'scheduled')
        .sort(
          (a, b) =>
            Date.parse(b.started_at ?? b.due_at ?? b.enqueued_at) -
            Date.parse(a.started_at ?? a.due_at ?? a.enqueued_at),
        ),
    [runs],
  );

  const waitingRuns = useMemo(
    () => historyRows.filter((run) => isReadyToCharge(run)),
    [historyRows],
  );

  const nextEs = scheduled.find((run) => run.type === InvoiceRunType.Es);
  const nextDefault = scheduled.find(
    (run) => run.type === InvoiceRunType.Default,
  );
  const laterScheduled = scheduled.filter(
    (run) => run !== nextEs && run !== nextDefault,
  );

  const alsoScheduledText = useMemo(() => {
    const dated = laterScheduled.filter((run) => run.due_at != null);
    if (dated.length === 0) return null;
    const parts = dated.map((run, i) =>
      i === dated.length - 1
        ? formatDateLong(run.due_at as string)
        : formatDayMonth(run.due_at as string),
    );
    return `Also scheduled: ${parts.join(', ')}. Runs are created three months ahead.`;
  }, [laterScheduled]);

  const startMenuButton = (
    <GatedButton capability={capability} variant="outline">
      <Play className="tw:size-4" />
      Start a run
      <ChevronDown className="tw:size-4" />
    </GatedButton>
  );

  return (
    <div className="tw:mx-auto tw:flex tw:max-w-[1320px] tw:flex-col tw:gap-6 tw:pb-10">
      <div className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-4">
        <div className="tw:flex tw:max-w-2xl tw:flex-col tw:gap-1">
          <h1 className="tw:text-3xl tw:font-semibold">Monthly invoicing</h1>
          <p className="tw:text-sm tw:text-muted-foreground">
            Partner invoices are generated automatically each month and reviewed
            in Billomat. The only action taken here is completing and charging a
            finished run.
          </p>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          {anyActive && <LiveChip lastRefreshed={lastRefreshed} />}
          <CapabilityChip capability={capability} />
          {capability.canAct ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Play className="tw:size-4" />
                  Start a run
                  <ChevronDown className="tw:size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="tw:w-80">
                <p className="tw:px-2 tw:py-1.5 tw:text-xs tw:text-muted-foreground">
                  Runs start on their own. Use this only to catch up after a
                  failure or a missed schedule.
                </p>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => setStartType(InvoiceRunType.Es)}
                >
                  <div className="tw:flex tw:flex-col">
                    <span className="tw:font-medium">Spanish run</span>
                    <span className="tw:text-xs tw:text-muted-foreground">
                      Asks for the next invoice number
                    </span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setStartType(InvoiceRunType.Default)}
                >
                  <div className="tw:flex tw:flex-col">
                    <span className="tw:font-medium">Default run</span>
                    <span className="tw:text-xs tw:text-muted-foreground">
                      All countries except Spain · ~8 h
                    </span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            startMenuButton
          )}
        </div>
      </div>

      {loading && runs.length === 0 && (
        <div className="tw:flex tw:justify-center tw:py-16">
          <Spinner className="tw:size-6" />
        </div>
      )}

      {waitingRuns.map((run) => (
        <WaitingForYouCard
          key={run.id}
          run={run}
          capability={capability}
          onReview={() => history.push(Routes.monthlyInvoicingRun(run.id))}
          onCharge={() => setChargeRun(run)}
        />
      ))}

      {(nextEs || nextDefault) && (
        <section className="tw:flex tw:flex-col tw:gap-3">
          <div className="tw:flex tw:items-baseline tw:gap-2">
            <h2 className="tw:text-lg tw:font-semibold">Upcoming</h2>
            <span className="tw:text-sm tw:text-muted-foreground">
              Automatic — no action needed
            </span>
          </div>
          <div className="tw:grid tw:grid-cols-2 tw:gap-4">
            {nextEs && <UpcomingCard run={nextEs} />}
            {nextDefault && <UpcomingCard run={nextDefault} />}
          </div>
          {alsoScheduledText && (
            <p className="tw:text-xs tw:text-muted-foreground">
              {alsoScheduledText}
            </p>
          )}
        </section>
      )}

      {(!loading || runs.length > 0) && (
        <RunHistoryTable runs={historyRows} nextScheduledRun={scheduled[0]} />
      )}

      <StartRunSheet
        type={startType}
        runs={runs}
        onOpenChange={(open) => {
          if (!open) setStartType(null);
        }}
        onStarted={() => void refetch()}
      />

      <ChargeModal
        run={chargeRun}
        onOpenChange={(open) => {
          if (!open) setChargeRun(null);
        }}
        onCharged={() => void refetch()}
      />
    </div>
  );
}
